import { useEffect, useRef, useState } from 'react';
import { SISTER_STORES } from '@/mocks/content';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function SisterStores() {
  const { ref, inView } = useInView(0.1);

  return (
    <section id="sister" className="relative bg-[#0A0A0F] py-24 md:py-36 overflow-hidden">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #C9A84C 0px, #C9A84C 1px, transparent 1px, transparent 60px)',
        }}
      />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="text-center mb-16">
          <p
            className="text-[#C9A84C] text-xs tracking-[0.6em] mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          >
            SISTER STORES
          </p>
          <h2
            className="text-white text-3xl md:text-4xl font-bold tracking-widest"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            系列店舗のご案内
          </h2>
          <div className="flex items-center justify-center gap-4 mt-6">
            <span
              className="h-px bg-[#C9A84C]/40 transition-all duration-1000"
              style={{ width: inView ? '60px' : '0px' }}
            />
            <i className="ri-store-2-line text-[#C9A84C]/50 text-sm" />
            <span
              className="h-px bg-[#C9A84C]/40 transition-all duration-1000"
              style={{ width: inView ? '60px' : '0px' }}
            />
          </div>
          <p
            className="text-white/40 text-xs mt-4 tracking-widest"
            style={{ fontFamily: "'Shippori Mincho', serif" }}
          >
            同じ富山・総曲輪エリアで展開する、グループ系列店をご紹介します。
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {SISTER_STORES.map((store, i) => (
            <div
              key={i}
              className="group relative overflow-hidden rounded-lg border border-white/10 hover:border-[#C9A84C]/40 transition-all duration-500"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(40px)',
                transition: `opacity 0.8s ease ${i * 150}ms, transform 0.8s ease ${i * 150}ms, border-color 0.5s`,
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              {/* Image area */}
              <div className="relative w-full h-52 md:h-64 overflow-hidden">
                <img
                  src={store.image}
                  alt={`${store.nameDisplay ?? store.name} 店内`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-[#0A0A0F]/40 to-transparent" />

                {/* Genre badge */}
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span
                    className="px-3 py-1 text-xs tracking-widest border"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      color: store.accentColor,
                      borderColor: `${store.accentColor}60`,
                      background: `${store.accentColor}15`,
                    }}
                  >
                    {store.genreJa}
                  </span>
                  {store.recruitNote && (
                    <span className="px-3 py-1 text-xs tracking-widest border border-white/20 text-white/60 bg-white/5"
                      style={{ fontFamily: "'Shippori Mincho', serif" }}
                    >
                      <i className="ri-user-add-line mr-1 text-xs" />
                      {store.recruitNote}
                    </span>
                  )}
                </div>

                {/* Store name overlay */}
                <div className="absolute bottom-4 left-5 right-5">
                  <p
                    className="text-xs tracking-[0.4em] mb-1"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontStyle: 'italic',
                      color: store.accentColor,
                    }}
                  >
                    {store.nameEn}
                  </p>
                  <h3
                    className="text-white text-2xl md:text-3xl font-bold tracking-widest"
                    style={{ fontFamily: "'Shippori Mincho', serif" }}
                  >
                    {store.nameDisplay ?? store.name}
                    {store.subName && (
                      <span className="text-base ml-2 text-white/60 font-normal">
                        -{store.subName}-
                      </span>
                    )}
                  </h3>
                </div>
              </div>

              {/* Info area */}
              <div className="p-5 md:p-6">
                <p
                  className="text-white/60 text-sm leading-[2] mb-5"
                  style={{ fontFamily: "'Shippori Mincho', serif" }}
                >
                  {store.concept}
                </p>

                <div className="space-y-2 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-line text-xs" style={{ color: store.accentColor }} />
                    </div>
                    <p
                      className="text-white/40 text-xs tracking-widest leading-[1.8]"
                      style={{ fontFamily: "'Shippori Mincho', serif" }}
                    >
                      {store.address}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-3 flex-wrap">
                  <a
                    href={store.url}
                    className="flex items-center gap-2 px-5 py-2.5 text-xs tracking-widest border transition-all duration-300 whitespace-nowrap cursor-pointer hover:opacity-80"
                    style={{
                      fontFamily: "'Shippori Mincho', serif",
                      color: store.accentColor,
                      borderColor: `${store.accentColor}50`,
                      background: `${store.accentColor}10`,
                    }}
                  >
                    <i className="ri-external-link-line text-xs" />
                    公式サイトへ
                  </a>
                  {store.instagram && (
                    <a
                      href={store.instagram}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="flex items-center gap-2 px-5 py-2.5 text-xs tracking-widest border border-white/15 text-white/50 hover:text-white/80 hover:border-white/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
                      style={{ fontFamily: "'Shippori Mincho', serif" }}
                    >
                      <i className="ri-instagram-line text-xs" />
                      Instagram
                    </a>
                  )}
                </div>
              </div>

              {/* Hover glow */}
              <div
                className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ boxShadow: `inset 0 0 30px ${store.accentColor}08` }}
              />
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <p
          className="text-center text-white/20 text-xs mt-12 tracking-widest"
          style={{ fontFamily: "'Shippori Mincho', serif" }}
        >
          各店舗の詳細は、それぞれの公式サイト・SNSをご確認ください。
        </p>
      </div>
    </section>
  );
}
